// src/scenes/PerformanceDashboard/index.js
import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Modal,
  Divider,
  Chip,
  useTheme,
  Autocomplete,
  IconButton,
  TextField,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  TrendingUp,
  Group,
  AttachMoney,
  Assessment,
  Close,
  ShowChart,
  BarChart,
  PieChart as PieChartIcon,
  MoneyOff,
  AccountBalance,
  CompareArrows,
  People,
  Timeline,
  MonetizationOn,
} from "@mui/icons-material";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { tokens } from "../../theme";
import { companyManagementUrl, userManagementUrl } from "../../config";
import { DirectionsCar } from "@mui/icons-material";
import { AssignmentReturn } from "@mui/icons-material";
import Dashboard from "../PerfomanceDashboard"; // Adjust path if necessary
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../context/TourContext";
import { PictureAsPdf } from '@mui/icons-material';
import PDFDownloadButton from "../Component/PDFDownloadButton";

const baseUrl2 = companyManagementUrl.uri;
const baseUrl3 = userManagementUrl.uri;

const COLORS = ["#3078AC", "#C6D3CD"];

// Role configuration for dynamic rendering
const roleConfig = {
  1: { showRegion: true, showBranch: true, showFO: true }, // Admin
  2: { showRegion: true, showBranch: true, showFO: true }, // Regional Manager
  3: { showRegion: false, showBranch: true, showFO: true }, // Branch Manager
  4: { showRegion: false, showBranch: false, showFO: true }, // Field Officer
};

// Define tour steps for PerformanceDashboard
const performanceDashboardTourSteps = [
  {
    target: ".performance-dashboard-container",
    content: "Welcome to the Performance Dashboard! This is your central hub for analyzing loan performance metrics across regions, branches, and field officers.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: ".performance-region-filter",
    content: "Use the Region filter to view loan metrics for a specific region. This is useful for regional managers to focus on their area.",
    placement: "bottom",
    condition: (props) => props.roleConfig[props.roleGroupId]?.showRegion,
  },
  {
    target: ".performance-branch-filter",
    content: "Select a Branch to drill down into branch-specific loan performance data.",
    placement: "bottom",
    condition: (props) => props.roleConfig[props.roleGroupId]?.showBranch,
  },
  {
    target: ".performance-fo-filter",
    content: "Choose a Field Officer (RO) to monitor their individual loan portfolio performance.",
    placement: "bottom",
    condition: (props) => props.roleConfig[props.roleGroupId]?.showFO,
  },
  {
    target: ".performance-selection-message",
    content: "Select a Region, Branch, or Field Officer to display detailed performance metrics below.",
    placement: "bottom",
  },
  {
    target: ".performance-dashboard",
    content: "After applying filters, this section displays key loan metrics, including Disbursed Amount, Collection Rate, Portfolio at Risk, and an Executive Summary of performance indicators.",
    placement: "top",
  },
];

const PerformanceDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const roleGroupId = parseInt(decodedToken.RoleGroupId);
  const [selectedData, setSelectedData] = useState(null);
  const [loadData, setLoadData] = useState({
    p_companyid: null,
    p_isregion: null,
    p_isbranch: null,
    p_rolegroupid: null,
    p_fieldofficerid: null,
    p_branchid: null,
    p_regionid: null,
    p_isfieldofficer: null,
  });
  const [regionData, setRegionData] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [filteredBranchData, setFilteredBranchData] = useState([]);
  const [FOData, setFOData] = useState([]);
  const [filteredFOData, setFilteredFOData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedFO, setSelectedFO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [error, setError] = useState(null);
  const [showAllCards, setShowAllCards] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  // Tour state
  const { isFirstTime, touredPages, markPageAsToured } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const currentPath = window.location.pathname;
  // Inside the PerformanceDashboard component, add state for dashboard data
const [dashboardMetrics, setDashboardMetrics] = useState(null);

  useEffect(() => {
    if (isFirstTime && !touredPages.includes(currentPath)) {
      setShowModal(true);
    }
  }, [isFirstTime, touredPages, currentPath]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      markPageAsToured(currentPath);
      setShowModal(false);
    }
  };

  const startTour = () => {
    setRunTour(true);
    setShowModal(false);
  };

  const cancelTour = () => {
    setShowModal(false);
    markPageAsToured(currentPath);
  };

  // Create a function to update dashboard metrics
const updateDashboardMetrics = (metrics) => {
  setDashboardMetrics(metrics);
};

  // Fetch dropdown data (Regions, Branches, Field Officers)
  useEffect(() => {
    async function fetchDropdownData() {
      try {
        const rolegroup = { isActive: 1, roleGroupId: 5 };
        const [res1, res2, res3] = await Promise.all([
          fetchJsonDynamic("region", null, null, baseUrl2),
          fetchJsonDynamic("branch", null, null, baseUrl2),
          fetchJsonDynamic("allUsersByColumnValues", "usermodel", rolegroup, baseUrl3),
        ]);

        const regions = res1?.results ? JSON.parse(res1.results) : [];
        const branches = res2?.results ? JSON.parse(res2.results) : [];
        const officers = res3?.results ? JSON.parse(res3.results) : [];

        setRegionData(regions);
        setBranchData(branches);
        setFilteredBranchData(branches);
        setFOData(officers);
        setFilteredFOData(officers);
      } catch (error) {
        setError("Failed to load dropdown data. Please try again later.");
        console.error("Error fetching dropdown data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchDropdownData();
  }, []);

  // Update branch options when region changes
  useEffect(() => {
    if (selectedRegion) {
      const filteredBranches = branchData.filter(
        (branch) => branch.RegionId === selectedRegion.value
      );
      setFilteredBranchData(filteredBranches);
      setSelectedBranch(null);
      setSelectedFO(null);
      setFilteredFOData(FOData);
    } else {
      setFilteredBranchData(branchData);
      setSelectedBranch(null);
      setSelectedFO(null);
      setFilteredFOData(FOData);
    }
  }, [selectedRegion, branchData, FOData]);

  // Update FO options when branch changes
  useEffect(() => {
    if (selectedBranch) {
      const filteredOfficers = FOData.filter(
        (officer) => officer.BranchId === selectedBranch.value
      );
      setFilteredFOData(filteredOfficers);
      setSelectedFO(null);
    } else if (selectedRegion) {
      const filteredOfficers = FOData.filter(
        (officer) => officer.RegionId === selectedRegion.value
      );
      setFilteredFOData(filteredOfficers);
      setSelectedFO(null);
    } else {
      setFilteredFOData(FOData);
      setSelectedFO(null);
    }
  }, [selectedBranch, selectedRegion, FOData]);

  // Dropdown options
  const regionOptions = regionData?.map((item) => ({
    value: item.Id,
    label: item.Name,
  })) || [];

  const branchOptions = filteredBranchData?.map((item) => ({
    value: item.Id,
    label: item.Name,
  })) || [];

  const FOOptions = filteredFOData
    ?.filter((item) => item.RoleGroupId === 5)
    .map((item) => ({
      value: item.Id,
      label: `${item.FirstName} ${item.LastName}`,
    })) || [];

    // Update the filters object to pass to PDF
const pdfFilters = {
  region: selectedRegion?.label || '',
  branch: selectedBranch?.label || '',
  fieldOfficer: selectedFO?.label || '',
};

  // Handle Field Officer selection
  const handleFOChange = (event, newValue) => {
    setSelectedFO(newValue);
    if (newValue) {
      const updatedData = {
        p_companyid: null,
        p_isregion: 0,
        p_isbranch: 0,
        p_isfieldofficer: 1,
        p_rolegroupid: 0,
        p_fieldofficerid: newValue.value,
        p_branchid: 0,
        p_regionid: 0,
      };
      setLoadData(updatedData);
    } else {
      setLoadData({
        p_companyid: null,
        p_isregion: null,
        p_isbranch: null,
        p_rolegroupid: null,
        p_fieldofficerid: null,
        p_branchid: null,
        p_regionid: null,
        p_isfieldofficer: null,
      });
    }
  };

  // Handle Branch selection
  const handleBranchChange = (event, newValue) => {
    setSelectedBranch(newValue);
    setSelectedFO(null);
    if (newValue) {
      const updatedData = {
        p_companyid: null,
        p_isregion: 0,
        p_isbranch: 1,
        p_isfieldofficer: 0,
        p_rolegroupid: 0,
        p_fieldofficerid: 0,
        p_branchid: newValue.value,
        p_regionid: 0,
      };
      setLoadData(updatedData);
    } else {
      setLoadData({
        p_companyid: null,
        p_isregion: null,
        p_isbranch: null,
        p_rolegroupid: null,
        p_fieldofficerid: null,
        p_branchid: null,
        p_regionid: null,
        p_isfieldofficer: null,
      });
    }
  };

  // Handle Region selection
  const handleRegionChange = (event, newValue) => {
    setSelectedRegion(newValue);
    setSelectedBranch(null);
    setSelectedFO(null);
    if (newValue) {
      const updatedData = {
        p_companyid: null,
        p_isregion: 1,
        p_isbranch: 0,
        p_isfieldofficer: 0,
        p_rolegroupid: 0,
        p_fieldofficerid: 0,
        p_branchid: 0,
        p_regionid: newValue.value,
      };
      setLoadData(updatedData);
    } else {
      setLoadData({
        p_companyid: null,
        p_isregion: null,
        p_isbranch: null,
        p_rolegroupid: null,
        p_fieldofficerid: null,
        p_branchid: null,
        p_regionid: null,
        p_isfieldofficer: null,
      });
    }
  };

  return (
    <Box
      sx={{ width: "100%", bgcolor: colors.primary[400], minHeight: "100vh", p: 2 }}
      className="performance-dashboard-container"
    >
      {/* Joyride Tour */}
      <Joyride
        steps={performanceDashboardTourSteps.filter((step) =>
          step.condition ? step.condition({ roleConfig, roleGroupId }) : true
        )}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: colors.blueAccent[500],
            backgroundColor: colors.primary[400],
            textColor: colors.grey[100],
            zIndex: 1000,
          },
        }}
      />

      {/* First-Time Modal */}
      <Dialog open={showModal} onClose={cancelTour}>
        <DialogTitle>Welcome to the Performance Dashboard!</DialogTitle>
        <DialogContent>
          This dashboard provides insights into loan performance metrics. Would you like a guided tour to explore its features?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelTour}>Skip</Button>
          <Button onClick={startTour} variant="contained">
            Start Tour
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Message */}
      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {/* Dropdown Header */}
      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={4} mb={1} className="performance-filters">
        {/* Region Dropdown */}
        {roleConfig[roleGroupId]?.showRegion && (
          <Autocomplete
            options={regionOptions}
            loading={loading}
            value={selectedRegion}
            onChange={handleRegionChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Region"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                className="performance-region-filter"
                aria-label="Region Filter"
              />
            )}
          />
        )}

        {/* Branch Dropdown */}
        {roleConfig[roleGroupId]?.showBranch && (
          <Autocomplete
            options={branchOptions}
            loading={loading}
            value={selectedBranch}
            onChange={handleBranchChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Branch"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                className="performance-branch-filter"
                aria-label="Branch Filter"
              />
            )}
          />
        )}

        {/* Field Officer Dropdown */}
        {roleConfig[roleGroupId]?.showFO && (
          <Autocomplete
            options={FOOptions}
            loading={loading}
            value={selectedFO}
            onChange={handleFOChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select RO"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                className="performance-fo-filter"
                aria-label="Field Officer Filter"
              />
            )}
          />
        )}
      </Box>

      {/* Add the PDF button in the UI (after the dropdown filters) */}
<Box display="flex" justifyContent="flex-end" mb={2}>
  <PDFDownloadButton 
    data={dashboardMetrics} 
    filters={pdfFilters}
    disabled={!selectedFO && !selectedBranch && !selectedRegion}
  />
</Box>

      {/* Dashboard Grid */}
      <Box sx={{ mt: 2 }} className="performance-selection-message">
        {!selectedFO && !selectedBranch && !selectedRegion && (
          <Typography variant="h6" sx={{ textAlign: "center", color: colors.grey[500] }}>
            Please select a region, branch, or RO to proceed.
          </Typography>
        )}
      </Box>

      {/* Dashboard component with dynamic data */}
      <Box className="performance-dashboard">
       <Dashboard 
  datum={dashboardData || loadData} 
  suppressTour={runTour}
  onMetricsUpdate={updateDashboardMetrics}
/>
      </Box>
    </Box>
  );
};

export default PerformanceDashboard;